<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main'
import PageHeader from '@components/page-header'
import AreaHeatmap from '@components/area-heatmap'
import axios from 'axios'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faAngleUp,faAngleDown,faDollarSign,faBoxDollar,faReceipt,faEye,faComputerMouseScrollwheel,faPercent } from '@fortawesome/pro-regular-svg-icons'
library.add(faAngleUp,faAngleDown,faDollarSign,faBoxDollar,faReceipt,faEye,faComputerMouseScrollwheel,faPercent);

import {
  REGAL_EYES_ADVERTISER_ID,
  REGAL_EYES_CAMPAIGN_ID,
  REGAL_EYES_CAMPAIGN_NAME,
  REGAL_EYES_TOTALS,
  REGAL_EYES_BUDGET,
  REGAL_EYES_DAYS_REMAINING,
  REGAL_EYES_DEMOGRAPHICS,
  REGAL_EYES_PLATFORMS,
  REGAL_EYES_PLATFORM_MONTHLY,
  REGAL_EYES_MONTHS,
  REGAL_EYES_MONTHLY_TOTALS,
  RIYADH_AREA_DATA,
} from '@utils/regal-eyes'



export default {
	page: {
		title: 'Campaigns - Edit Campaign',
		meta: [{ name: 'description', content: appConfig.description }],
	},
	components: { Layout, PageHeader, FontAwesomeIcon, AreaHeatmap },
	computed: {
		isRegalEyesCampaign() {
			return this.$route.params.id === REGAL_EYES_CAMPAIGN_ID;
		},
		genderDonut() {
			return this.buildDonut(this.demographics.gender, ['#f06292', '#5369f8', '#9ca3af']);
		},
		deviceDonut() {
			return this.buildDonut(this.demographics.device, ['#43d39e', '#5369f8', '#ffbe0b']);
		},
		osDonut() {
			return this.buildDonut(this.demographics.os, ['#1d2939', '#a4cb56', '#9ca3af']);
		},
		placementDonut() {
			return this.buildDonut(this.demographics.placement, ['#5369f8', '#f77e53', '#43d39e', '#9ca3af']);
		},
		ageBar() {
			const slice = this.demographics.age;
			return {
				series: [{ name: 'Audience %', data: slice.map((d) => d.value) }],
				chartOptions: {
					chart: { toolbar: { show: false } },
					plotOptions: { bar: { horizontal: true, borderRadius: 3 } },
					dataLabels: { enabled: true, formatter: (v) => v + '%' },
					colors: ['#55b78d'],
					xaxis: {
						categories: slice.map((d) => d.label),
						labels: { formatter: (v) => v + '%' },
						axisBorder: { color: '#d6ddea' },
						axisTicks: { color: '#d6ddea' },
					},
					grid: { borderColor: '#f1f3fa' },
					tooltip: { theme: 'dark', y: { formatter: (v) => v + '%' } },
				},
			};
		},
		platformDonut() {
			const platforms = REGAL_EYES_PLATFORMS;
			const palette = ['#1877f2', '#fffc00', '#000000', '#34a853', '#4285f4', '#5369f8'];
			return {
				series: platforms.map((p) => Number(p.spend.toFixed(2))),
				chartOptions: {
					labels: platforms.map((p) => p.platform),
					colors: palette,
					legend: { position: 'bottom' },
					dataLabels: {
						enabled: true,
						formatter: (val) => (typeof val === 'number' ? val.toFixed(1) + '%' : val),
					},
					plotOptions: {
						pie: { donut: { size: '60%' }, expandOnClick: false },
					},
					tooltip: {
						y: {
							formatter: (v) =>
								Number(v).toLocaleString(undefined, {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								}),
						},
					},
				},
			};
		},
		monthlyStackedBar() {
			const months = REGAL_EYES_MONTHS;
			const rows = REGAL_EYES_PLATFORM_MONTHLY;
			const palette = ['#1877f2', '#fffc00', '#000000', '#34a853', '#4285f4', '#5369f8'];
			return {
				series: rows.map((row) => ({
					name: row.platform,
					data: months.map((m) => Number((row[m] || 0).toFixed(2))),
				})),
				chartOptions: {
					chart: {
						type: 'bar',
						stacked: true,
						toolbar: { show: false },
					},
					plotOptions: {
						bar: { horizontal: false, columnWidth: '45%', borderRadius: 4 },
					},
					colors: palette,
					dataLabels: { enabled: false },
					xaxis: {
						categories: months,
						labels: { style: { fontSize: '12px' } },
					},
					yaxis: {
						labels: {
							formatter: (v) =>
								Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }),
						},
					},
					legend: { position: 'bottom' },
					grid: { borderColor: '#f1f3fa' },
					tooltip: {
						shared: true,
						y: {
							formatter: (v) =>
								Number(v).toLocaleString(undefined, {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								}),
						},
					},
				},
			};
		},
	},
	methods:
	{
		formatNumber (num) {
    		return parseFloat(num).toFixed(2)
  		},
		formatInt(val) {
			if (val === null || val === undefined || isNaN(val)) return '-'
			return Number(val).toLocaleString()
		},
		formatMoney(val) {
			if (val === null || val === undefined) return '-'
			// Accept either raw numbers or pre-formatted strings ("32,460.97").
			const num = typeof val === 'string' ? Number(val.replace(/,/g, '')) : Number(val)
			if (isNaN(num)) return '-'
			return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
		},
		getMonthlyPlatformSpend(platformIndex, month) {
			const row = REGAL_EYES_PLATFORM_MONTHLY[platformIndex]
			return row ? row[month] : 0
		},
		formatPct(val) {
			if (val === null || val === undefined || isNaN(val)) return '-'
			return (Number(val) * 100).toFixed(3) + '%'
		},
		setHeatmapMetric(metricKey) {
			this.heatmapMetric = metricKey
		},
		buildDonut(slice, colors) {
			return {
				series: slice.map((d) => d.value),
				chartOptions: {
					labels: slice.map((d) => d.label),
					colors: colors,
					legend: { position: 'bottom' },
					dataLabels: {
						enabled: true,
						formatter: (v) => (typeof v === 'number' ? v.toFixed(0) + '%' : v),
					},
					plotOptions: {
						pie: {
							donut: { size: '65%' },
							expandOnClick: false,
						},
					},
					tooltip: { y: { formatter: (v) => v + '%' } },
					responsive: [
						{ breakpoint: 480, options: { legend: { position: 'bottom' } } },
					],
				},
			};
		},
		NewLineitem: function()
		{

			//if(this.campaign.type != "youtube")
			//{

				if(typeof this.PageComponents.selected_advertiser.id !== 'number' || parseInt(this.PageComponents.selected_advertiser.id) == 0)
				{
				this.$bvModal.msgBoxOk("Please select your advertiser first" , {titleHtml: "<b>Error<b>"});
				}
				else
				{

				this.PageComponents.create_new_button = 'Please wait ..';
				this.PageComponents.draft_lineitem_loading = true;
				
				axios.post(appConfig.api+'campaigns/draft_lineitem/', {uinfo:JSON.parse(localStorage.getItem("auth.currentUser")),advertiser:this.PageComponents.selected_advertiser.id,campaign_id:this.$route.params.id})
				.then((response) =>
				{ 
					if(response.data)
					{
					if(response.data.status == false)
					{
						if(response.data.code == 401)
						{
						//invalud session
						this.$router.push('/logout');
						}
						else
						{
						this.PageComponents.draft_lineitem_loading = false;
						this.PageComponents.create_new_button = 'Create New';
						this.$bvModal.msgBoxOk("Sorry we are facing a problem in our servers. Please try again later", {titleHtml: "<b>Network Error<b>"});
						}
					}
					else
					{

						if(response.data.id !== undefined)
						{

							if(this.campaign.type != 'youtube')
							{
								this.$router.push('/campaigns/'+this.$route.params.id+'/lineitems/'+response.data.id);
							}
							else{
								this.$router.push('/campaigns/'+this.$route.params.id+'/youtube/'+response.data.id);
							}
							
							this.PageComponents.draft_lineitem_loading = false;
							this.PageComponents.create_new_button = 'Create New';
						}
						else
						{
							this.$bvModal.msgBoxOk("Sorry we are facing a problem in our servers. Please try again later", {titleHtml: "<b>Network Error<b>"});
						}
					}
					}
					else
					{
					this.$bvModal.msgBoxOk("Sorry we are facing a problem in our servers. Please try again later", {titleHtml: "<b>Network Error<b>"});
					}

				})
				.catch((error) =>
				{
					//other error
					this.$bvModal.msgBoxOk("Sorry we are facing a problem in our servers. Please try again later", {titleHtml: "<b>Network Error<b>"});
				});
				}

			//}
			//else
			//{
			//	alert('This feature is not available for this campaign type');
			//}
		},
		loadRegalEyesMockData: function()
		{
			var uinfo = JSON.parse(localStorage['auth.currentUser']);
			if (uinfo && uinfo.advertiser) {
				this.PageComponents.selected_advertiser = { name: uinfo.advertiser.name, id: uinfo.advertiser.id };
			}

			// Hard gate: only Regal Eyes is allowed to view this synthetic
			// campaign. Anyone else is bounced back to the campaigns list.
			var currentAdvertiserId = Number(this.PageComponents.selected_advertiser.id) || 0;
			if (currentAdvertiserId !== REGAL_EYES_ADVERTISER_ID) {
				this.$router.replace('/campaigns');
				return;
			}

			function numberWithCommas(x) {
				if (x == null || isNaN(x)) return '0';
				return Number(x).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
			}
			function fmt2(x) {
				return Number(x).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
			}
			function pct(x) {
				return (Number(x) * 100).toFixed(3) + '%';
			}

			this.campaign = {
				id: REGAL_EYES_CAMPAIGN_ID,
				name: REGAL_EYES_CAMPAIGN_NAME,
				type: 'social',
				start_time: 'Feb 1 2026',
				end_time: 'Apr 30 2026',
			};

			this.rtb = {
				campaign_budget: fmt2(REGAL_EYES_BUDGET),
				campaign_spend: fmt2(REGAL_EYES_TOTALS.spend),
				campaign_remaining: fmt2(REGAL_EYES_BUDGET - REGAL_EYES_TOTALS.spend),
				days_remaining: REGAL_EYES_DAYS_REMAINING,
				lifetime_impressions: REGAL_EYES_TOTALS.impressions,
				lifetime_clicks: REGAL_EYES_TOTALS.clicks,
				lifetime_CTR: REGAL_EYES_TOTALS.ctr,
			};

			this.overviewData = [
				{ class: 'border-bottom py-4', icon: 'fa-regular fa-eye',                       value: numberWithCommas(REGAL_EYES_TOTALS.impressions), title: 'Total Impressions' },
				{ class: 'border-bottom py-4', icon: 'fa-regular fa-computer-mouse-scrollwheel', value: numberWithCommas(REGAL_EYES_TOTALS.clicks),      title: 'Total Clicks' },
				{ class: 'py-4',               icon: 'fa-regular fa-percent',                   value: pct(REGAL_EYES_TOTALS.ctr),                      title: 'CTR' },
			];

			// Per-area breakdown for the heatmap + table.
			this.areaBreakdown = RIYADH_AREA_DATA.map(function (row) {
				return Object.assign({}, row, {
					ctr: row.impressions > 0 ? row.clicks / row.impressions : 0,
				});
			});

			// Demographic chart inputs.
			this.demographics = REGAL_EYES_DEMOGRAPHICS;

			// Platform + monthly breakdown (Regal Eyes Report Latest 18May).
			this.platformBreakdown = REGAL_EYES_PLATFORMS;
			this.monthlyTotals = REGAL_EYES_MONTHLY_TOTALS;

			// No backend line items; just skip the table.
			this.LineItemsList = [];
			this.PageComponents.lineitems_loading = false;
			this.PageComponents.loading = false;
		},
		get_campaign_deatils: function()
		{

			var uinfo = JSON.parse(localStorage['auth.currentUser']);
			if(uinfo.advertiser !== undefined){
				this.PageComponents.selected_advertiser = {name:uinfo.advertiser.name,id:uinfo.advertiser.id};
			}
			else
			{
				this.PageComponents.selected_advertiser = {name:'',id:0};
			}

			this.PageComponents.loading = true;

			axios.post(appConfig.api+'campaigns/details/', {uinfo:JSON.parse(localStorage.getItem("auth.currentUser")),id:this.$route.params.id,advertiser:this.PageComponents.selected_advertiser.id})
			.then((response) =>
			{ 
				this.PageComponents.loading = false;

				if(response.data)
				{
				if(response.data.status == false)
				{
					//invalud session
					this.$router.push('/logout');
				}
				else
				{

					function numberWithCommas(x) {
						if(x == null){
							return 0;	
						}
						else{
							return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
						}
					}
					//success Status
					this.campaign = response.data.details;
					this.rtb 	  = response.data.rtb;
					
					this.overviewData = 
						[{
							class: 'border-bottom py-4',
							icon: 'fa-regular fa-eye',
							value: numberWithCommas(response.data.rtb.lifetime_impressions),
							title: 'Total Impressions',
						},
						{
							class: 'border-bottom py-4',
							icon: 'fa-regular fa-computer-mouse-scrollwheel',
							value:  numberWithCommas(response.data.rtb.lifetime_clicks),
							title: 'Total Clicks',
						},
						{
							class: 'py-4',
							icon: 'fa-regular fa-percent',
							value: numberWithCommas( response.data.rtb.lifetime_CTR),
							title: 'CTR',
						}];



						this.todayData = 
						[{
							class: 'border-bottom py-4',
							icon: '',
							value: numberWithCommas(response.data.rtb.today_impressions),
							title: 'Total Impressions',
						},
						{
							class: 'border-bottom py-4',
							icon: '',
							value:  numberWithCommas(response.data.rtb.today_clicks),
							title: 'Total Clicks',
						},
						{
							class: 'py-4',
							icon: '',
							value: numberWithCommas( response.data.rtb.today_CTR),
							title: 'CTR',
						}];



						this.last7DaysData = 
						[{
							class: 'border-bottom py-4',
							icon: '',
							value: numberWithCommas(response.data.rtb['7_day_impressions']),
							title: 'Total Impressions',
						},
						{
							class: 'border-bottom py-4',
							icon: '',
							value:  numberWithCommas(response.data.rtb['7_day_clicks']),
							title: 'Total Clicks',
						},
						{
							class: 'py-4',
							icon: '',
							value: numberWithCommas( response.data.rtb['7_day_CTR']),
							title: 'CTR',
						}];

						this.monthtodateData = 
						[{
							class: 'border-bottom py-4',
							icon: '',
							value: numberWithCommas(response.data.rtb['30_day_impressions']),
							title: 'Total Impressions',
						},
						{
							class: 'border-bottom py-4',
							icon: '',
							value:  numberWithCommas(response.data.rtb['30_day_clicks']),
							title: 'Total Clicks',
						},
						{
							class: 'py-4',
							icon: '',
							value: numberWithCommas( response.data.rtb['30_day_CTR']),
							title: 'CTR',
						}];


						
				}
				}
				else
				{
					this.$bvModal.msgBoxOk("Sorry we are facing a problem in our servers. Please try again later", {titleHtml: "<b>Network Error<b>"});
				}


			})
			.catch((error) =>
			{
				//other error
				this.$bvModal.msgBoxOk("Sorry we are facing a problem in our servers. Please try again later", {titleHtml: "<b>Network Error<b>"});
			})
		},
		edit_lineitem: function(campaign,id){
      	   this.$router.push('/campaigns/'+campaign+'/lineitems/'+id);
    	},
		edit_youtube: function(campaign,id){
			this.$router.push('/campaigns/'+campaign+'/youtube/'+id);
		},
		get_lineitems_list: function()
		{

			var uinfo = JSON.parse(localStorage['auth.currentUser']);
			if(uinfo.advertiser !== undefined){
				this.PageComponents.selected_advertiser = {name:uinfo.advertiser.name,id:uinfo.advertiser.id};
			}
			else
			{
				this.PageComponents.selected_advertiser = {name:'',id:0};
			}

			this.PageComponents.lineitems_loading = true;

			axios.post(appConfig.api+'campaigns/lineitem_list/', {uinfo:JSON.parse(localStorage.getItem("auth.currentUser")),id:this.$route.params.id,advertiser:this.PageComponents.selected_advertiser.id})
			.then((response) =>
			{ 
				this.PageComponents.lineitems_loading = false;

				if(response.data)
				{
					if(response.data.status == false)
					{
						//invalud session
						this.$router.push('/logout');
					}
					else
					{
						this.LineItemsList = response.data.list;
					
					}
				}
				else
				{
					this.$bvModal.msgBoxOk("Sorry we are facing a problem in our servers. Please try again later", {titleHtml: "<b>Network Error<b>"});
				}


			})
			.catch((error) =>
			{
				//other error
				this.$bvModal.msgBoxOk("Sorry we are facing a problem in our servers. Please try again later", {titleHtml: "<b>Network Error<b>"});
			})
		},

  	},
	created: function() 
	{
	 if (this.isRegalEyesCampaign) {
	   this.loadRegalEyesMockData();
	   return;
	 }
	 this.get_campaign_deatils();
	 this.get_lineitems_list();
  	},
	data() 
	{
		function getDaysInMonth(month, year) {
  const date = new Date(year, month, 1)
  const days = []
  let idx = 0
  while (date.getMonth() === month && idx < 15) {
    const d = new Date(date)
    days.push(d.getDate() + ' ' + d.toLocaleString('en-us', { month: 'short' }))
    date.setDate(date.getDate() + 1)
    idx += 1
  }
  return days
}
const now = new Date()
const labels = getDaysInMonth(now.getMonth(), now.getFullYear())

		return {
			campaign: {},
			rtb:{},
			areaBreakdown: [],
			heatmapMetric: 'spend',
			heatmapMetrics: [
				{ key: 'spend',       label: 'Spend' },
				{ key: 'impressions', label: 'Impressions' },
				{ key: 'clicks',      label: 'Clicks' },
			],
			demographics: { gender: [], age: [], device: [], os: [], placement: [] },
			platformBreakdown: [],
			monthlyTotals: [],
			PageComponents:{
				hasError:false,
				errorMsgs:[],
				loading:false,
				lineitems_loading:true,
				draft_lineitem_loading:false,
				create_new_button:'Create New',
				selected_advertiser:{id:0,name:''}
			},
			title: 'Campaigns - Campaign Details',
			items: [
				{
					text: 'Dashboard',
					href: '/',
				},
				{
					text: 'Campaigns',
					href: '/campaigns/list',
				},
				{
					text: 'Campaign Details',
					active: true,
				},
			],
			overviewData: [],
			todayData: [],
			last7DaysData: [],
			monthtodateData: [],
		
			LineItemsList:[],
			listFields:[
				{ key: 'id', label: 'ID', sortable: false},
				{ key: 'name', label: 'Name', sortable: true, sortDirection: 'desc' },
				{ key: 'is_active', label: 'Status', sortable: true },
				{ key:'budget',label:'Budget',sortable:true},
				{ key:'spend',label:'Spent',sortable:true},
				{ key:'remaining',label:'Remaining',sortable:true},
				{ key:'impressions',label:'Impressions',sortable:true},
				{ key: 'clicks', label: 'Clicks',sortable: true },
				{ key: 'ctr', label: 'CTR',sortable: false },
				{key:'viewability',label:'Viewability',sortable: false},
				{key:'action_id',label:'Action',sortable: false}
			]
		}
	},

}
</script>

<template>
	<Layout>
		<PageHeader :title="title" :items="items" />

		<b-alert variant="danger" show dismissible v-show="PageComponents.hasError">
			Please correct the following error(s):
			<ul>
				<li v-for="error in PageComponents.errorMsgs">{{ error }}</li>
			</ul>
		</b-alert>


		<div class="col-12" v-show="PageComponents.loading">
			<div class="text-center" style="margin: auto; padding-top: 23px;">
				<div class="text-muted font-size-13">Please wait we are submiting your information ...</div>
				<b-spinner
					label="Loading..."
					variant="primary"
					class="ml-3"
				></b-spinner>
			</div>
		</div>

		<div v-show="!PageComponents.loading">

			<div class="row">
				<div class="col-md-12 col-xl-12">
					<div class="card">
						<div class="card-body p-0">
						<div class="media p-3">
							<div class="media-body">
							<span class="text-muted text-uppercase font-size-12 font-weight-bold">Campaign Details</span>
							

							<table class="table table-borderless mb-0 text-muted">
								<tbody>
									<tr>
										<th scope="row" class="col-1">Name</th>
										<td>{{campaign.name}}</td>
										
										<td class="text-right">

											<b-link v-if="!isRegalEyesCampaign" :href="'/campaigns/edit/' + campaign.id">Edit Campaign</b-link>
										</td>
									</tr>
								</tbody>
							</table>


							</div>
						</div>
						</div>
					</div>
				</div>
			</div>	

			<div class="row">
				<div class="col-md-6 col-xl-3">
					<div class="card">
						<div class="card-body p-0">
						<div class="media p-3">
							<div class="media-body">
							<span class="text-muted text-uppercase font-size-12 font-weight-bold">Total Budget</span>
							<h2 class="mb-0">{{rtb.campaign_budget}} USD</h2>
							</div>
						</div>
						</div>
					</div>
				</div>
				<div class="col-md-6 col-xl-3">
					<div class="card">
						<div class="card-body p-0">
						<div class="media p-3">
							<div class="media-body">
							<span class="text-muted text-uppercase font-size-12 font-weight-bold">Total Spent</span>
							<h2 class="mb-0">{{rtb.campaign_spend}} USD</h2>
							</div>
						</div>
						</div>
					</div>
				</div>
				<div class="col-md-6 col-xl-3">
					<div class="card">
						<div class="card-body p-0">
						<div class="media p-3">
							<div class="media-body">
							<span class="text-muted text-uppercase font-size-12 font-weight-bold">Remaining</span>
							<h2 class="mb-0">{{ rtb.campaign_remaining ? rtb.campaign_remaining : formatNumber(rtb.campaign_budget - rtb.campaign_spend) }} USD</h2>
							</div>
							
						</div>
						</div>
					</div>
				</div>
				<div class="col-md-6 col-xl-3">
					<div class="card">
						<div class="card-body p-0">
						<div class="media p-3">
							<div class="media-body">
							<span class="text-muted text-uppercase font-size-12 font-weight-bold">Remaining Days</span>
							<h2 class="mb-0">{{rtb.days_remaining}}</h2>
							</div>
						</div>
						</div>
					</div>
				</div>
			</div>


			<div class="row">

				<div :class="isRegalEyesCampaign ? 'col-xl-12' : 'col-xl-3'">
					<div class="card">
						<div class="card-body p-0">
							<h5 class="card-title header-title border-bottom p-3 mb-0">Overview</h5>

							<div v-if="!isRegalEyesCampaign">
								<div v-for="item in overviewData" :key="item.title" class="media px-3" :class="item.class">
									<font-awesome-icon size="2x" :icon="item.icon" />
									<div class="media-body ml-4">
										<h4 class="mt-0 mb-1 font-size-22 font-weight-normal">{{item.value}}</h4>
										<span class="text-muted">{{ item.title }}</span>
									</div>
								</div>
							</div>

							<div v-if="isRegalEyesCampaign" class="row p-3">
								<div v-for="item in overviewData" :key="item.title" class="col-md-4 d-flex align-items-center">
									<font-awesome-icon size="2x" :icon="item.icon" class="text-muted" />
									<div class="ml-3">
										<h4 class="mt-0 mb-1 font-size-22 font-weight-normal">{{item.value}}</h4>
										<span class="text-muted">{{ item.title }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="col-xl-3" v-if="!isRegalEyesCampaign">
					<div class="card">
						<div class="card-body p-0">
							<h5 class="card-title header-title border-bottom p-3 mb-0">Today</h5>

							<div v-for="item in todayData" :key="item.title" class="media px-3" :class="item.class">
								<div class="media-body">
									<h4 class="mt-0 mb-1 font-size-22 font-weight-normal">{{item.value}}</h4>
									<span class="text-muted">{{ item.title }}</span>
								</div>
								<font-awesome-icon size="2x" :icon="item.icon" />
							</div>
						</div>
					</div>
				</div>

				<div class="col-xl-3" v-if="!isRegalEyesCampaign">
					<div class="card">
						<div class="card-body p-0">
							<h5 class="card-title header-title border-bottom p-3 mb-0">Last 7 Days</h5>

							<div v-for="item in last7DaysData" :key="item.title" class="media px-3" :class="item.class">
								<div class="media-body">
									<h4 class="mt-0 mb-1 font-size-22 font-weight-normal">{{item.value}}</h4>
									<span class="text-muted">{{ item.title }}</span>
								</div>
								<font-awesome-icon size="2x" :icon="item.icon" />
							</div>
						</div>
					</div>
				</div>

				<div class="col-xl-3" v-if="!isRegalEyesCampaign">
					<div class="card">
						<div class="card-body p-0">
							<h5 class="card-title header-title border-bottom p-3 mb-0">Last 30 Days</h5>

							<div v-for="item in monthtodateData" :key="item.title" class="media px-3" :class="item.class">
								<div class="media-body">
									<h4 class="mt-0 mb-1 font-size-22 font-weight-normal">{{item.value}}</h4>
									<span class="text-muted">{{ item.title }}</span>
								</div>
								<font-awesome-icon size="2x" :icon="item.icon" />
							</div>
						</div>
					</div>
				</div>

			</div>

			<!-- Regal Eyes: spend distribution (platform share + monthly trend) -->
			<div class="row" v-if="isRegalEyesCampaign">
				<div class="col-xl-12">
					<div class="card">
						<div class="card-body p-0">
							<h5 class="card-title header-title border-bottom p-3 mb-0">Spend by Month</h5>
							<div class="row p-3 m-0">
								<div v-for="m in monthlyTotals" :key="m.month" class="col-md-4 d-flex align-items-center">
									<font-awesome-icon size="2x" :icon="['far', 'box-dollar']" class="text-muted" />
									<div class="ml-3">
										<h4 class="mt-0 mb-1 font-size-22 font-weight-normal">{{ formatMoney(m.spend) }} USD</h4>
										<span class="text-muted">{{ m.month }} 2026</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="col-xl-5">
					<div class="card">
						<div class="card-body">
							<h5 class="card-title mt-0 mb-3">Spend by Platform</h5>
							<apexchart type="donut" height="320" :series="platformDonut.series" :options="platformDonut.chartOptions" />
						</div>
					</div>
				</div>

				<div class="col-xl-7">
					<div class="card">
						<div class="card-body">
							<h5 class="card-title mt-0 mb-3">Monthly Spend Trend</h5>
							<apexchart type="bar" height="320" :series="monthlyStackedBar.series" :options="monthlyStackedBar.chartOptions" />
						</div>
					</div>
				</div>

				<div class="col-xl-12">
					<div class="card">
						<div class="card-body">
							<h5 class="header-title mb-3">Spend by Platform &amp; Month</h5>
							<div class="table-responsive">
								<table class="table table-sm table-hover mb-0">
									<thead>
										<tr>
											<th>Platform</th>
											<th class="text-right">Feb</th>
											<th class="text-right">Mar</th>
											<th class="text-right">Apr</th>
											<th class="text-right">Total</th>
											<th class="text-right">Share</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(row, i) in platformBreakdown" :key="row.platform">
											<td>{{ row.platform }}</td>
											<td class="text-right">{{ formatMoney(getMonthlyPlatformSpend(i, 'Feb')) }}</td>
											<td class="text-right">{{ formatMoney(getMonthlyPlatformSpend(i, 'Mar')) }}</td>
											<td class="text-right">{{ formatMoney(getMonthlyPlatformSpend(i, 'Apr')) }}</td>
											<td class="text-right">{{ formatMoney(row.spend) }}</td>
											<td class="text-right">{{ (row.share * 100).toFixed(1) }}%</td>
										</tr>
									</tbody>
									<tfoot>
										<tr class="font-weight-bold">
											<td>Total</td>
											<td class="text-right">{{ formatMoney(monthlyTotals[0] && monthlyTotals[0].spend) }}</td>
											<td class="text-right">{{ formatMoney(monthlyTotals[1] && monthlyTotals[1].spend) }}</td>
											<td class="text-right">{{ formatMoney(monthlyTotals[2] && monthlyTotals[2].spend) }}</td>
											<td class="text-right">{{ formatMoney(rtb.campaign_spend) }}</td>
											<td class="text-right">100.0%</td>
										</tr>
									</tfoot>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Regal Eyes: audience demographics -->
			<div class="row" v-if="isRegalEyesCampaign">
				<div class="col-xl-4">
					<div class="card">
						<div class="card-body">
							<h5 class="card-title mt-0 mb-3">Gender</h5>
							<apexchart type="donut" height="300" :series="genderDonut.series" :options="genderDonut.chartOptions" />
						</div>
					</div>
				</div>

				<div class="col-xl-8">
					<div class="card">
						<div class="card-body">
							<h5 class="card-title mt-0 mb-3">Age Groups</h5>
							<apexchart type="bar" height="300" :series="ageBar.series" :options="ageBar.chartOptions" />
						</div>
					</div>
				</div>
			</div>

			<div class="row" v-if="isRegalEyesCampaign">
				<div class="col-xl-4">
					<div class="card">
						<div class="card-body">
							<h5 class="card-title mt-0 mb-3">Device Types</h5>
							<apexchart type="donut" height="280" :series="deviceDonut.series" :options="deviceDonut.chartOptions" />
						</div>
					</div>
				</div>

				<div class="col-xl-4">
					<div class="card">
						<div class="card-body">
							<h5 class="card-title mt-0 mb-3">Operating System</h5>
							<apexchart type="donut" height="280" :series="osDonut.series" :options="osDonut.chartOptions" />
						</div>
					</div>
				</div>

				<div class="col-xl-4">
					<div class="card">
						<div class="card-body">
							<h5 class="card-title mt-0 mb-3">Placement</h5>
							<apexchart type="donut" height="280" :series="placementDonut.series" :options="placementDonut.chartOptions" />
						</div>
					</div>
				</div>
			</div>
			<!-- /Regal Eyes demographics -->

			<div class="row">
				<div class="card" style="background: inherit;">
					<div class="card-body">
					</div>
				</div>
			</div>	

			<!-- Regal Eyes: spend heatmap + per-area breakdown -->
			<div class="row" v-if="isRegalEyesCampaign">
				<div class="col-12">
					<div class="card">
						<div class="card-body">
							<div class="row align-items-center mb-3">
								<div class="col">
									<h4 class="header-title mt-0 mb-1">Spend Heatmap</h4>
									<p class="text-muted mb-0">Delivery across Riyadh districts</p>
								</div>
							</div>

							<div class="d-flex align-items-center mb-2">
								<span class="text-muted mr-2 font-size-13">Heatmap by:</span>
								<b-button-group size="sm">
									<b-button
										v-for="m in heatmapMetrics"
										:key="m.key"
										:variant="heatmapMetric === m.key ? 'primary' : 'outline-primary'"
										@click="setHeatmapMetric(m.key)"
									>
										{{ m.label }}
									</b-button>
								</b-button-group>
							</div>

							<area-heatmap :data="areaBreakdown" :metric="heatmapMetric" height="520px" />
						</div>
					</div>

					<div class="card">
						<div class="card-body">
							<h5 class="header-title mb-3">Delivery by Area</h5>
							<div class="table-responsive">
								<table class="table table-sm table-hover mb-0">
									<thead>
										<tr>
											<th>Area</th>
											<th class="text-right">Spend</th>
											<th class="text-right">Impressions</th>
											<th class="text-right">CTR</th>
											<th class="text-right">Clicks</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="row in areaBreakdown" :key="row.area">
											<td>{{ row.area }}</td>
											<td class="text-right">{{ formatMoney(row.spend) }}</td>
											<td class="text-right">{{ formatInt(row.impressions) }}</td>
											<td class="text-right">{{ formatPct(row.ctr) }}</td>
											<td class="text-right">{{ formatInt(row.clicks) }}</td>
										</tr>
									</tbody>
									<tfoot>
										<tr class="font-weight-bold">
											<td>Total</td>
											<td class="text-right">{{ formatMoney(rtb.campaign_spend) }}</td>
											<td class="text-right">{{ formatInt(rtb.lifetime_impressions) }}</td>
											<td class="text-right">{{ formatPct(rtb.lifetime_CTR) }}</td>
											<td class="text-right">{{ formatInt(rtb.lifetime_clicks) }}</td>
										</tr>
									</tfoot>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
			<!-- /Regal Eyes block -->

			<div class="row" v-if="!isRegalEyesCampaign">
				<div class="col-12">
					<div class="card">
						<div class="card-body">

							<div class="card">
								<div class="card-body">
									<div class="row align-items-center">
										<div class="col">
											<h4 class="header-title mt-0 mb-1">Lineitems</h4>
										</div>
										<div class="col text-right">
											<b-button variant="success" @click="NewLineitem" :disabled="PageComponents.draft_lineitem_loading">{{PageComponents.create_new_button}}</b-button>
										</div>
									</div>
								</div>
								<!-- end card body-->
							</div>


							<b-table :items="LineItemsList" :fields="listFields" hover :busy="PageComponents.lineitems_loading" show-empty>
								<template #table-busy>
									<div class="text-center text-danger my-2">
									  <b-spinner class="align-middle" variant="primary"></b-spinner>
									  <div class="text-muted font-size-13">Please wait ...</div>
									</div>
								  </template>  

								  <template #empty="LineItemsList">
										<p class="text-center">You don't have any line items yet for this campaign</p>
								  </template>

								  <template #cell(name)="row">
										<b-link v-if="row.item.type != 'youtube'" v-on:click="edit_lineitem(row.item.campaign_id,row.item.action_id);">{{ row.item.name }}</b-link>
										<b-link v-if="row.item.type == 'youtube'" v-on:click="edit_youtube(row.item.campaign_id,row.item.action_id);">{{ row.item.name }}</b-link>
                  				  </template>


								  <template #cell(is_active)="row">
									
									<span v-if="row.item.status" class="badge bg-primary" style="color: white;font-weight: bold;text-transform: capitalize;">Active</span>
									<span v-if="!row.item.status" class="badge bg-danger" style="color: white;font-weight: bold;text-transform: capitalize;">Inactive</span>

								  </template>  


								  <template #cell(action_id)="row">
										<b-link v-if="row.item.type != 'youtube'" v-on:click="edit_lineitem(row.item.campaign_id,row.item.action_id);">Edit</b-link>
										<b-link v-if="row.item.type == 'youtube'" v-on:click="edit_youtube(row.item.campaign_id,row.item.action_id);">Edit</b-link>

                  				  </template>

							</b-table>	
						</div>
					</div>
				</div>

			</div>

		</div>
	

	</Layout>
</template>
